import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Scheduling|Accommodation|';
const sharedProperties: Pick<LocDef, 'shared' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.ExamAccommodations,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateSchedulingAccommodation';
export type SPCandidateSchedulingAccommodationLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ContentExpired`
	| `${LocNameStart}LabelAccommodation`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelDocumentation`
	| `${LocNameStart}LabelFilterByStatus`
	| `${LocNameStart}ButtonRequest`
	| `${LocNameStart}ContentViewInstructions`
	| `${LocNameStart}ContentModifyInstructions`;

export const spCandidateSchedulingAccommodationLocs: LocDefs<SPCandidateSchedulingAccommodationLocNames> =
	{
		SPCandidateSchedulingAccommodationTitleTab: {
			defaultValue: 'Accommodations',
			description: 'Tab title for the accommodation page',
			displayPath: `${pathStart}Tab title`,
			displayType: 'Title',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Title.Tab',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationTitlePage: {
			defaultValue: 'Accommodations',
			description: 'Page title for the accommodation page',
			displayPath: `${pathStart}Page title`,
			displayType: 'Title',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Title.Tab',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationContentExpired: {
			defaultValue: 'Expired',
			description: 'Expired text for an accommodation',
			displayPath: `${pathStart}Expired`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Content.Expired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationLabelAccommodation: {
			defaultValue: 'Accommodation',
			description: 'Column heading for: Accommodation.',
			displayPath: `${pathStart}Accommodation`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.ColumnHeading.Accommodation',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationLabelStatus: {
			defaultValue: 'Status',
			description: 'Column heading for: Status.',
			displayPath: `${pathStart}Status`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.ColumnHeading.Status',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationLabelExpireDate: {
			defaultValue: 'Expire Date',
			description: 'Column heading for: Expire Date.',
			displayPath: `${pathStart}Expire Date`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.ColumnHeading.ExpireDate',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationLabelDocumentation: {
			defaultValue: 'Documentation',
			description: 'Column heading for: Documentation.',
			displayPath: `${pathStart}Documentation`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.ColumnHeading.Documentation',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationLabelFilterByStatus: {
			defaultValue: 'Filter by Status',
			description: 'Label for: Filter by Status.',
			displayPath: `${pathStart}Filter by Status`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.StatusFilter',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationButtonRequest: {
			defaultValue: 'Request Accommodation',
			description: 'Button text for: Request Accommodation.',
			displayPath: `${pathStart}Request Accommodation`,
			displayType: 'Button',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Button.Request',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationContentViewInstructions: {
			defaultValue:
				'<p>Review your approved exam accommodations and request new accommodations.</p>',
			description: 'Instructions for viewing accommodations.',
			displayPath: `${pathStart}View Instructions`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Content.ViewInstructions',
			htmlAllowed: true,
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationContentModifyInstructions: {
			defaultValue: `<p><strong>Scheduling an Appointment with Accommodations</strong>
				<br />
				If you need to request an exam accommodation, please wait to receive your approval
				before scheduling your exam. You will have to cancel any currently scheduled exams to
				receive accommodations, as accommodations must be pre-approved.
			</p>`,
			description: 'Instructions for modifying accommodations.',
			displayPath: `${pathStart}Modify Instructions`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Content.ModifyInstructions',
			htmlAllowed: true,
			...sharedProperties,
		},
	};

export type SPCandidateSchedulingAccommodationLocType =
	LocMap<SPCandidateSchedulingAccommodationLocNames>;
